// Currency formatting utility for locale-specific number formatting
// Maps currency codes to their appropriate locale and formatting rules

// Currency code to locale mapping
const CURRENCY_LOCALE_MAP = {
  // Major currencies with specific locales
  USD: 'en-US', // United States Dollar
  EUR: 'de-DE', // Euro (using German formatting as default)
  GBP: 'en-GB', // British Pound
  JPY: 'ja-JP', // Japanese Yen
  CNY: 'zh-CN', // Chinese Yuan
  INR: 'en-IN', // Indian Rupee
  CAD: 'en-CA', // Canadian Dollar
  AUD: 'en-AU', // Australian Dollar
  CHF: 'de-CH', // Swiss Franc
  SEK: 'sv-SE', // Swedish Krona
  NOK: 'nb-NO', // Norwegian Krone
  DKK: 'da-DK', // Danish Krone
  KRW: 'ko-KR', // South Korean Won
  SGD: 'en-SG', // Singapore Dollar
  HKD: 'zh-HK', // Hong Kong Dollar
  NZD: 'en-NZ', // New Zealand Dollar
  MXN: 'es-MX', // Mexican Peso
  BRL: 'pt-BR', // Brazilian Real
  RUB: 'ru-RU', // Russian Ruble
  ZAR: 'en-ZA', // South African Rand
  TRY: 'tr-TR', // Turkish Lira
  PLN: 'pl-PL', // Polish Złoty
  CZK: 'cs-CZ', // Czech Koruna
  HUF: 'hu-HU', // Hungarian Forint
  RON: 'ro-RO', // Romanian Leu
  BGN: 'bg-BG', // Bulgarian Lev
  HRK: 'hr-HR', // Croatian Kuna
  ISK: 'is-IS', // Icelandic Króna

  // Asian currencies
  THB: 'th-TH', // Thai Baht
  VND: 'vi-VN', // Vietnamese Đồng
  IDR: 'id-ID', // Indonesian Rupiah
  MYR: 'ms-MY', // Malaysian Ringgit
  PHP: 'fil-PH', // Philippine Peso

  // Middle Eastern currencies
  AED: 'en-US', // UAE Dirham - hack: use en-US to avoid Arabic characters for better UI visibility
  SAR: 'en-US', // Saudi Riyal - hack: use en-US to avoid Arabic characters for better UI visibility
  QAR: 'en-US', // Qatari Riyal - hack: use en-US to avoid Arabic characters for better UI visibility
  KWD: 'en-US', // Kuwaiti Dinar - hack: use en-US to avoid Arabic characters for better UI visibility
  BHD: 'en-US', // Bahraini Dinar - hack: use en-US to avoid Arabic characters for better UI visibility
  OMR: 'en-US', // Omani Rial - hack: use en-US to avoid Arabic characters for better UI visibility
  JOD: 'en-US', // Jordanian Dinar - hack: use en-US to avoid Arabic characters for better UI visibility
  LBP: 'en-US', // Lebanese Pound - hack: use en-US to avoid Arabic characters for better UI visibility
  ILS: 'he-IL', // Israeli New Shekel

  // African currencies
  EGP: 'en-US', // Egyptian Pound - hack: use en-US to avoid Arabic characters for better UI visibility
  NGN: 'en-NG', // Nigerian Naira
  KES: 'sw-KE', // Kenyan Shilling
  GHS: 'en-GH', // Ghanaian Cedi

  // Latin American currencies
  ARS: 'es-AR', // Argentine Peso
  CLP: 'es-CL', // Chilean Peso
  COP: 'es-CO', // Colombian Peso
  PEN: 'es-PE', // Peruvian Sol
  UYU: 'es-UY', // Uruguayan Peso

  // Default fallback for other currencies
  DEFAULT: 'en-US',
};

// Extract currency code from currency string (e.g., "US Dollar (USD)" -> "USD")
const extractCurrencyCode = (currencyString) => {
  if (!currencyString) return null;

  // Match pattern like "Currency Name (CODE)"
  const match = currencyString.match(/\(([A-Z]{3})\)$/);
  return match ? match[1] : null;
};

// Get locale for a currency
const getLocaleForCurrency = (currencyString) => {
  const currencyCode = extractCurrencyCode(currencyString);
  return CURRENCY_LOCALE_MAP[currencyCode] || CURRENCY_LOCALE_MAP.DEFAULT;
};

// Format number with currency-specific locale
const formatCurrencyNumber = (
  value,
  currencyString,
  skipCurrencyExtraction = false
) => {
  if (!value || value === '') return '';

  // Clean the value - remove all non-numeric characters except decimal points
  const numericValue = value.toString().replace(/[^\d.]/g, '');
  const parsedValue = parseFloat(numericValue);

  if (isNaN(parsedValue)) return '';

  const locale = skipCurrencyExtraction
    ? CURRENCY_LOCALE_MAP[currencyString] || CURRENCY_LOCALE_MAP.DEFAULT
    : getLocaleForCurrency(currencyString);

  try {
    // Use Intl.NumberFormat for locale-specific formatting
    const formatter = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return formatter.format(Math.floor(parsedValue));
  } catch (error) {
    // Fallback to comma formatting if locale is not supported
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
};

// Clean currency input (remove formatting, keep only numbers)
export const cleanCurrencyInput = (value) => {
  if (!value) return '';
  return value.toString().replace(/[^\d]/g, '');
};

export const currencyFormatterHelpers = {
  formatCurrencyNumber,
  cleanCurrencyInput,
  getLocaleForCurrency,
  extractCurrencyCode,
};
